import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { loadMaps } from "../utils/loadMaps";
import "../styles/NearbyGyms.css";

// Local GIF helper (expects files in /public/exercises/<name>.gif)
const GIF = (name) => `/exercises/${name}.gif`;

const MANUAL_GIFS = {
  // very-low-impact (BMI >= 30)
  "chair-sit-to-stand": GIF("chair-sit-to-stand"),
  "marching-in-place": GIF("marching-in-place"),
  "step-ups-low": GIF("step-ups-low"),
  "walking-treadmill": GIF("walking-treadmill"),
  "band-seated-row": GIF("band-seated-row"),
  "wall-push-up": GIF("wall-push-up"),
  // low-impact (25 ≤ BMI < 30)
  "elliptical-low": GIF("elliptical-low"),
  "cycling-stationary": GIF("cycling-stationary"),
  "rower-machine": GIF("rower-machine"),
  "step-ups": GIF("step-ups"),
  "band-row": GIF("band-row"),
  "chair-squat": GIF("chair-squat"),
  "plank-knees": GIF("plank-knees"),
  // moderate (18.5 ≤ BMI < 25)
  "push-up": GIF("push-up"),
  "back-squat": GIF("back-squat"),
  "romanian-deadlift": GIF("romanian-deadlift"),
  "plank": GIF("plank"),
  "bird-dog": GIF("bird-dog"),
  "walking-lunge": GIF("walking-lunge"),
  "db-row": GIF("db-row"),
  "oh-press-dumbbell": GIF("oh-press-dumbbell"),
  "farmer-carry": GIF("farmer-carry"),
  // gentle (BMI < 18.5)
  "band-row-gentle": GIF("band-row-gentle"),
  "goblet-squat-light": GIF("goblet-squat-light"),
  "glute-bridge": GIF("glute-bridge"),
  "knee-push-up": GIF("knee-push-up"),
  "bird-dog-gentle": GIF("bird-dog-gentle"),
  "superman-alt": GIF("superman-alt"),
};

function suggestionsForBMI(bmi) {
  if (bmi < 18.5) {
    return [
      { key: "knee-push-up", label: "Push-up (knees)" },
      { key: "goblet-squat-light", label: "Goblet Squat (light)" },
      { key: "glute-bridge", label: "Glute Bridge" },
      { key: "band-row-gentle", label: "Band Row (gentle)" },
      { key: "bird-dog-gentle", label: "Bird Dog (gentle)" },
      { key: "superman-alt", label: "Alt Superman" },
    ];
  }
  if (bmi < 25) {
    return [
      { key: "push-up", label: "Push-up" },
      { key: "back-squat", label: "Back Squat" },
      { key: "romanian-deadlift", label: "RDL" },
      { key: "plank", label: "Plank" },
      { key: "db-row", label: "DB Row" },
      { key: "oh-press-dumbbell", label: "OH Press (DB)" },
      { key: "farmer-carry", label: "Farmer Carry" },
      { key: "bird-dog", label: "Bird Dog" },
      { key: "walking-lunge", label: "Lunge" },
    ];
  }
  if (bmi < 30) {
    return [
      { key: "elliptical-low", label: "Elliptical" },
      { key: "cycling-stationary", label: "Bike" },
      { key: "rower-machine", label: "Rower" },
      { key: "step-ups", label: "Step-ups" },
      { key: "band-row", label: "Band Row" },
      { key: "chair-squat", label: "Chair Squat" },
      { key: "plank-knees", label: "Plank (knees)" },
    ];
  }
  return [
    { key: "chair-sit-to-stand", label: "Sit-to-Stand" },
    { key: "marching-in-place", label: "March in Place" },
    { key: "step-ups-low", label: "Low Step-ups" },
    { key: "walking-treadmill", label: "Treadmill Walk" },
    { key: "band-seated-row", label: "Band Seated Row" },
    { key: "wall-push-up", label: "Wall Push-up" },
  ];
}

export default function NearbyGyms() {
  const mapElRef = useRef(null);
  const markersRef = useRef([]);
  const placesServiceRef = useRef(null);
  const mapRef = useRef(null);

  const [gyms, setGyms] = useState([]);
  const [error, setError] = useState("");
  const [loadingMap, setLoadingMap] = useState(true);

  const { state } = useLocation();
  const navigate = useNavigate();

  // Back handler: mimic browser back, with a safe fallback
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/result");
  };

  const bmi = useMemo(() => {
    const s = Number(state?.bmi);
    if (Number.isFinite(s) && s > 0) return s;
    const ss = Number(sessionStorage.getItem("lastBMI"));
    if (Number.isFinite(ss) && ss > 0) return ss;
    const ls = Number(localStorage.getItem("lastBMI"));
    if (Number.isFinite(ls) && ls > 0) return ls;
    return 22;
  }, [state?.bmi]);

  const exerciseSuggestions = useMemo(
    () => suggestionsForBMI(bmi).map((x, i) => ({ ...x, url: MANUAL_GIFS[x.key], _k: `${x.key}-${i}` })),
    [bmi]
  );

  const addMarker = (g, position, title) => {
    const m = new g.maps.Marker({ map: mapRef.current, position, title });
    markersRef.current.push(m);
  };

  const normalize = (arr) => {
    const byId = new Map();
    for (const p of arr) {
      if (!p?.place_id) continue;
      if (!byId.has(p.place_id)) byId.set(p.place_id, p);
    }
    return Array.from(byId.values()).sort(
      (a, b) => (b.rating || 0) - (a.rating || 0) || String(a.name).localeCompare(String(b.name))
    );
  };

  useEffect(() => {
    let cancelled = false;

    const clearMarkers = () => {
      markersRef.current.forEach((m) => m?.setMap?.(null));
      markersRef.current = [];
    };

    const init = async () => {
      try {
        setLoadingMap(true);
        setError("");

        const g = await loadMaps();
        if (cancelled) return;

        const coords = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos.coords),
            (err) => reject(err),
            { enableHighAccuracy: false, maximumAge: 60000, timeout: 10000 }
          );
        });
        if (cancelled) return;

        const center = { lat: coords.latitude, lng: coords.longitude };

        mapRef.current = new g.maps.Map(mapElRef.current, {
          center,
          zoom: 14,
          mapId: process.env.REACT_APP_MAP_ID || undefined,
        });

        addMarker(g, center, "You are here");

        const svc = (placesServiceRef.current =
          placesServiceRef.current || new g.maps.places.PlacesService(mapRef.current));

        const nearbyReq = { location: center, radius: 8000, type: "gym" };

        const nearbyResults = await new Promise((resolve) => {
          svc.nearbySearch(nearbyReq, (res, status) => {
            if (status === g.maps.places.PlacesServiceStatus.OK || status === "OK") {
              resolve(res || []);
            } else {
              resolve([]);
            }
          });
        });

        const merged = normalize(nearbyResults);

        clearMarkers();
        const items = merged.map((p) => {
          const loc = p.geometry?.location;
          if (loc) addMarker(g, loc, p.name);
          return {
            placeId: p.place_id,
            name: p.name,
            address: p.formatted_address || p.vicinity || "",
            rating: p.rating || "N/A",
          };
        });

        if (!cancelled) {
          setGyms(items);
          if (!items.length) setError("No gyms found nearby.");
        }
      } catch {
        if (!cancelled) {
          setError("Unable to load map or nearby gyms.");
          setGyms([]);
        }
      } finally {
        if (!cancelled) setLoadingMap(false);
      }
    };

    init();
    return () => {
      cancelled = true;
      markersRef.current.forEach((m) => m?.setMap?.(null));
      markersRef.current = [];
      placesServiceRef.current = null;
      mapRef.current = null;
    };
  }, []);

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div className="gy-header">
        <div style={{ display: "flex", gap: 10, fontWeight: 700 }}>
          <span>Gyms</span><span>near</span><span>you</span>
        </div>
        <button className="gy-back-btn" onClick={goBack}>⬅ Back</button>
      </div>

      {error && <p role="alert" style={{ maxWidth: 1200, margin: "0 auto 16px", color: "#ffb4b4" }}>{error}</p>}

      {/* Map */}
      <div
        ref={mapElRef}
        style={{
          width: "100%",
          height: 450,
          margin: "0 auto 20px auto",
          maxWidth: 1200,
          background: "#0d0d0d",
          borderRadius: 10,
          border: "1px solid #073b4c",
        }}
        aria-busy={loadingMap ? "true" : "false"}
      />

      {/* Content below map */}
      <div className="gy-split">
        {/* Left: list */}
        <div className="gy-panel gy-list">
          <h3>Gyms near you</h3>
          {loadingMap && <p style={{ opacity: 0.8 }}>Loading map…</p>}
          <ul>
            {gyms.length ? (
              gyms.map((g) => (
                <li key={g.placeId}>
                  <div className="name">{g.name}</div>
                  <small className="meta">{g.address}</small>
                  <div className="stars">⭐ {g.rating}</div>
                </li>
              ))
            ) : (
              !loadingMap && <li>No gyms found nearby.</li>
            )}
          </ul>
        </div>

        {/* Right: suggestions */}
        <div className="gy-panel gy-picks">
          <h3>Suggested exercises — BMI {bmi.toFixed(1)}</h3>
          <div className="gy-picks-grid">
            {exerciseSuggestions.map((ex) => (
              <figure className="gy-figure" key={ex._k} title={ex.label}>
                {ex.url ? (
                  <img
                    src={ex.url}
                    alt={ex.label}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      const fallback = e.currentTarget.nextSibling;
                      if (fallback) fallback.style.display = "flex";
                    }}
                  />
                ) : null}
                <div className="fallback">No image</div>
                <figcaption>{ex.label}</figcaption>
              </figure>
            ))}
          </div>
          <small style={{ opacity: 0.65, display: "block", marginTop: 10 }}>
            These local GIFs are illustrative. Choose intensity that’s safe for you.
          </small>
        </div>
      </div>
    </div>
  );
}
